import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { CircleDotDashed } from "lucide-react";

import { AppShell, type XOPage } from "./components/AppShell";
import { AskPanel } from "./components/AskPanel";
import { XOLogo } from "./components/XOLogo";
import { DEMO_MODE, useAuth, useXO, XOProvider } from "./hooks";
import { SignInPage } from "./pages/SignInPage";

const BridgePage = lazy(() => import("./pages/BridgePage").then((module) => ({ default: module.BridgePage })));
const LogbookPage = lazy(() => import("./pages/LogbookPage").then((module) => ({ default: module.LogbookPage })));
const CoursePage = lazy(() => import("./pages/CoursePage").then((module) => ({ default: module.CoursePage })));
const ChartsPage = lazy(() => import("./pages/ChartsPage").then((module) => ({ default: module.ChartsPage })));
const SignalsPage = lazy(() => import("./pages/SignalsPage").then((module) => ({ default: module.SignalsPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((module) => ({ default: module.SettingsPage })));

const OWNER_EMAIL = "andresmd01@gmail.com";

const pageFromPath = (path: string): XOPage => {
  if (path.startsWith("/logbook")) return "logbook";
  if (path.startsWith("/course")) return "course";
  if (path.startsWith("/charts")) return "charts";
  if (path.startsWith("/signals")) return "signals";
  if (path.startsWith("/settings")) return "settings";
  return "bridge";
};

const pathForPage: Record<XOPage, string> = {
  bridge: "/",
  logbook: "/logbook",
  course: "/course",
  charts: "/charts",
  signals: "/signals",
  settings: "/settings",
};

const Loading = () => (
  <div className="xo-loading"><XOLogo /><CircleDotDashed /><span>XO is assembling the operational picture…</span></div>
);

const Workspace = ({ onSignOut }: { onSignOut: () => void }) => {
  const { data, error, loading } = useXO();
  const [page, setPage] = useState<XOPage>(() => pageFromPath(window.location.pathname));
  const [askOpen, setAskOpen] = useState(
    () => new URLSearchParams(window.location.search).get("ask") === "1",
  );

  useEffect(() => {
    const handle = () => setPage(pageFromPath(window.location.pathname));
    window.addEventListener("popstate", handle);
    return () => window.removeEventListener("popstate", handle);
  }, []);

  const navigate = (next: XOPage) => {
    window.history.pushState({}, "", pathForPage[next]);
    setPage(next);
  };

  const pendingSignals = data?.signals.filter((signal) => signal.status === "PENDING").length || 0;
  const currentSources = data?.sourceAccounts.filter((account) => account.status === "CURRENT").length || 0;
  const content = useMemo(() => {
    if (page === "logbook") return <LogbookPage />;
    if (page === "course") return <CoursePage />;
    if (page === "charts") return <ChartsPage />;
    if (page === "signals") return <SignalsPage />;
    if (page === "settings") return <SettingsPage />;
    return <BridgePage />;
  }, [page]);

  if (loading && !data) return <Loading />;
  if (error && !data) return <div className="fatal-error"><XOLogo /><h1>XO could not open.</h1><p>{error}</p></div>;

  return (
    <AppShell
      activePage={page}
      askOpen={askOpen}
      onAsk={() => setAskOpen(true)}
      onNavigate={navigate}
      onSignOut={onSignOut}
      pendingSignals={pendingSignals}
    >
      <Suspense fallback={<Loading />}>{content}</Suspense>
      <AskPanel attentionObjects={data?.attentionObjects || []} currentSources={currentSources} onClose={() => setAskOpen(false)} open={askOpen} />
    </AppShell>
  );
};

export const App = () => {
  const { loading, signIn, signOut, user } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);
  if (loading) return <Loading />;

  if (!DEMO_MODE && !user) {
    return <SignInPage error={authError} onSignIn={() => { setAuthError(null); void signIn().catch(() => setAuthError("XO could not complete Google sign-in.")); }} />;
  }
  if (!DEMO_MODE && user?.email !== OWNER_EMAIL) {
    return <SignInPage error="This Google account is not authorized for XO." onSignIn={() => void signOut()} />;
  }

  return (
    <XOProvider enabled={DEMO_MODE || Boolean(user)}>
      <Workspace onSignOut={() => void signOut()} />
    </XOProvider>
  );
};
